import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@clay-run/glaze/button';
import { Chip } from '@clay-run/glaze/chip';
import { CollapsibleCard, CollapsibleCardBody, CollapsibleCardHeader } from '@clay-run/glaze/collapsible-card';
import { CheckboxField } from '@clay-run/glaze/checkbox-field';
import { DebouncedSearchInput } from '@clay-run/glaze/debounced-search-input';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { Alert, AlertDescription } from '@clay-run/glaze/alert';
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogActions,
} from '@clay-run/glaze/alert-dialog';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Heading } from '@clay-run/glaze/heading';
import { Text } from '@clay-run/glaze/text';
// `/toast` has no flat `dist/toast.d.ts` — see index.tsx's import comment.
import { addToast } from '@clay-run/glaze/toast/index';
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { useApi, useCurrentUser } from '../../_shared/hooks';
import { LAUNCHER_CAMPAIGNS_QUERY_KEY } from '../../_shared/launcher-campaign-schema';
import {
  LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
  campaignWhenAndWhere,
  fetchCampaigns,
  type Campaign,
} from '../campaigns';
import { isInCampaignWindow } from '../campaign-window';
import { MAPPINGS_QUERY_KEY } from '../rep-mapping-wire';
import { mappingsQueryOptions, putMappings, sfdcUsersQueryOptions } from '../rep-mapping-client';
import {
  useLeadScannerStore,
  computeMapDiff,
  mkey,
  type MapDiff,
  type MapLens,
} from '../store';

/**
 * Settings screen: the rep↔event mapper (R4, U3 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md).
 *
 * Two lenses over ONE staged draft (`useLeadScannerStore`) — By event
 * (`CollapsibleCard` per in-window campaign, expanded body = mapped people
 * chips + a filterable `CheckboxField` checklist) and By person (the same
 * data pivoted). Switching lenses never touches the draft (R4) — see the
 * store's own tests for the same-reference proof.
 *
 * Copy is verbatim from docs/lead-scanner-prototype.html's mapper section
 * (~lines 1633-1800) per the prototype-copy-fidelity rule; DEV NOTE
 * narration in that file is not product copy.
 */

// The campaign schema, the `Campaign` view model and `campaignWhenAndWhere`
// used to be declared again right here, a fourth copy alongside `Home.tsx`,
// `ScanReview.tsx` and `ScansPage.tsx`'s shared `../campaigns` module. They
// now come from `../campaigns` (which parses with `_shared`'s one launcher
// campaign schema) — QA round 8, U2: a private copy of a cross-app wire shape
// is how this screen ended up writing a different shape than launcher's under
// launcher's own react-query key.

export default function RepEventMapper() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const currentUserEmail = user?.ownerEmail ?? user?.email ?? null;

  const savedKeys = useLeadScannerStore((s) => s.savedKeys);
  const mapDraft = useLeadScannerStore((s) => s.mapDraft);
  const mapLens = useLeadScannerStore((s) => s.mapLens);
  const mapQuery = useLeadScannerStore((s) => s.mapQuery);
  const syncMappings = useLeadScannerStore((s) => s.syncMappings);
  const toggleMapping = useLeadScannerStore((s) => s.toggleMapping);
  const discardMapDraft = useLeadScannerStore((s) => s.discardMapDraft);
  const commitSave = useLeadScannerStore((s) => s.commitSave);
  const setMapLens = useLeadScannerStore((s) => s.setMapLens);
  const setMapQuery = useLeadScannerStore((s) => s.setMapQuery);

  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // Per-card "filter people…" / "filter events…" text, keyed by the card's
  // own key (`c_<campaignId>` / `u_<email>`) — prototype's `state.mapFilter`.
  // Purely local presentational state (never shared, never asserted by a
  // test), so it stays component state rather than living in the store.
  const [cardFilters, setCardFilters] = useState<Record<string, string>>({});

  const mappingsQuery = useQuery(mappingsQueryOptions(apiFetch));

  const campaignsQuery = useQuery({
    // This app's OWN key — the value cached here is the slim `Campaign` view
    // model, not launcher's row (QA round 8, U2).
    queryKey: LEAD_SCANNER_CAMPAIGNS_QUERY_KEY,
    queryFn: async () => {
      // Sanctioned cross-app HTTP read (Context: "Launcher HTTP reads") — the
      // browser calls launcher's own route directly, same-origin, under the
      // rep's own auth. Lead-scanner's backend does NOT proxy this.
      return fetchCampaigns(apiFetch);
    },
  });

  const sfdcUsersQuery = useQuery(sfdcUsersQueryOptions(apiFetch));

  // Seed/refresh the store's `savedKeys` (and, on first load only, the
  // draft) whenever `/mappings` resolves — never on every render.
  useEffect(() => {
    if (mappingsQuery.data) syncMappings(mappingsQuery.data);
  }, [mappingsQuery.data, syncMappings]);

  const saveMutation = useMutation({
    // suppressErrorToast: this call site owns its own failure presentation
    // (Glaze `addErrorToast` below) rather than the app-wide generic toast —
    // see mini-apps/CLAUDE.md's `useApi`/`meta.suppressErrorToast` contract.
    meta: { suppressErrorToast: true },
    mutationFn: (diff: MapDiff) => putMappings(apiFetch, diff),
    onSuccess: (outcome, diff) => {
      // Either way the server's copy is the truth now: refresh it.
      void queryClient.invalidateQueries({ queryKey: MAPPINGS_QUERY_KEY });
      if (outcome === 'unconfirmed') {
        addToast({
          content: 'The save did not finish confirming. Mappings were refreshed from the server; check them and save again if needed.',
          status: 'info',
        });
        return;
      }
      // Commit THE DIFF THAT WAS SENT, not the live draft (§11.5) — an edit
      // the rep made while this PUT was in flight was never sent, so it has
      // to stay pending.
      commitSave(diff);
      addToast({
        content: `Mapping saved — ${diff.added.length} added · ${diff.removed.length} removed`,
        status: 'success',
      });
    },
    onError: (err) => {
      // Draft is deliberately retained (not reset) on failure — Save stays
      // re-enabled for a retry (plan's Error test scenario).
      addApiErrorToast({ content: 'Could not save the mapping. Try again.', error: err });
    },
  });

  // U14 / R13 — force a launcher campaign resync so an event created in
  // Salesforce moments ago is mappable now instead of at the next hourly
  // sync. Sanctioned cross-app HTTP write (mini-apps lane 2: the browser
  // calls launcher's own route directly under the rep's auth) — launcher's
  // `useTriggerCampaignSync` hook is deliberately NOT imported, since apps
  // never import from sibling apps.
  const syncCampaignsMutation = useMutation({
    // This call site owns its failure presentation, same as the save above.
    meta: { suppressErrorToast: true },
    mutationFn: async () => {
      // The route is fire-and-forget: it answers `{ok: true}` immediately and
      // runs the sync in the background, so there is no result shape worth
      // parsing and nothing to await beyond the 2xx `apiFetch` guarantees.
      await apiFetch('/api/tools/launcher/campaigns/sync', { method: 'POST' });
    },
    onSuccess: () => {
      // BOTH keys: this app's own entry (Home / ScanReview / ScansPage / this
      // screen), and launcher's shared entry — the sync rewrites
      // `launcher_sfdc_campaigns`, so Launcher, Playbooks and Playhouse are
      // looking at stale rows too until theirs is invalidated as well.
      void queryClient.invalidateQueries({ queryKey: LEAD_SCANNER_CAMPAIGNS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: LAUNCHER_CAMPAIGNS_QUERY_KEY });
      addToast({
        content: 'Syncing events from Salesforce — this runs in the background and can take a minute.',
        status: 'success',
      });
    },
    onError: (err) => {
      addApiErrorToast({ content: 'Could not start the Salesforce sync. Try again.', error: err });
    },
  });

  const windowedCampaigns = useMemo(() => {
    const now = new Date();
    return (campaignsQuery.data ?? [])
      .filter((c) => isInCampaignWindow(c.eventDate, now))
      .sort((a, b) => (a.eventDate ?? '').localeCompare(b.eventDate ?? ''));
  }, [campaignsQuery.data]);

  const reps = sfdcUsersQuery.data ?? [];

  const diff = mapDraft ? computeMapDiff(mapDraft, savedKeys) : { added: [], removed: [] };
  const pendingCount = diff.added.length + diff.removed.length;
  const eventCount = new Set(Array.from(savedKeys).map((k) => k.slice(k.indexOf('|') + 1))).size;

  const draftHas = (email: string, campaignId: string) => (mapDraft ?? savedKeys).has(mkey(email, campaignId));
  const isPending = (email: string, campaignId: string) => draftHas(email, campaignId) !== savedKeys.has(mkey(email, campaignId));

  const isLoading = mappingsQuery.isLoading || campaignsQuery.isLoading || sfdcUsersQuery.isLoading;

  function handleDiscardClick() {
    if (pendingCount > 0) setConfirmDiscardOpen(true);
  }
  function handleConfirmDiscard() {
    discardMapDraft();
    setConfirmDiscardOpen(false);
    addToast({ content: 'Changes discarded — nothing was saved', status: 'info' });
  }
  function handleSave() {
    if (pendingCount === 0) return;
    saveMutation.mutate(diff);
  }
  function handleLensChange(lens: MapLens) {
    setMapLens(lens);
  }

  const byEvent = mapLens === 'events';
  const trimmedQuery = mapQuery.trim().toLowerCase();

  const filteredCampaigns = windowedCampaigns.filter(
    (c) => !trimmedQuery || `${c.name} ${c.city ?? ''}`.toLowerCase().includes(trimmedQuery),
  );
  const filteredReps = reps.filter(
    (r) => !trimmedQuery || `${r.name} ${r.email} ${r.role ?? ''}`.toLowerCase().includes(trimmedQuery),
  );

  return (
    // `@container/mgr` (Tailwind v4 named container query) — a real CSS
    // `@container mgr (min-width: 760px)` rule, never `@media`, so the dev
    // mobile-frame preview measures the frame it's rendered in rather than
    // the browser window (plan R4 / prototype line ~373-409).
    <div className="@container/mgr flex flex-col gap-4">
      <div>
        <Heading size="xs">Rep ↔ event mapping</Heading>
        <Text size="sm" variant="secondary">
          Who is scanning at which event
        </Text>
      </div>

      <Alert status="info">
        <AlertDescription>
          Someone can be scanning at more than one event at once — a customer dinner and the
          conference around it. Map everyone scanning each event, then save. Reps choose which of
          their events they are scanning for on the home screen.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col gap-2 @min-[760px]/mgr:flex-row @min-[760px]/mgr:items-center">
        <SegmentedControl value={mapLens} onValueChange={handleLensChange} className="@min-[760px]/mgr:w-[212px]">
          <SegmentedControlOption value="events">By event</SegmentedControlOption>
          <SegmentedControlOption value="people">By person</SegmentedControlOption>
        </SegmentedControl>
        <DebouncedSearchInput
          className="flex-1"
          aria-label={byEvent ? 'Search events' : 'Search people'}
          placeholder={byEvent ? 'Search events…' : 'Search people…'}
          initialValue={mapQuery}
          onSearch={setMapQuery}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <Text size="xs" variant="tertiary">
          Events from the last three days onward.
        </Text>
        <Button
          variant="secondary"
          size="sm"
          loading={syncCampaignsMutation.isPending}
          onClick={() => syncCampaignsMutation.mutate()}
        >
          Sync from Salesforce
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {isLoading && (
          <Text size="sm" variant="tertiary">
            Loading…
          </Text>
        )}

        {!isLoading && byEvent && filteredCampaigns.length === 0 && (
          <Text size="sm" variant="tertiary">
            No events match “{mapQuery}”.
          </Text>
        )}
        {!isLoading && byEvent &&
          filteredCampaigns.map((c) => {
            const key = `c_${c.id}`;
            const mappedReps = reps.filter((r) => draftHas(r.email, c.id));
            const filterText = (cardFilters[key] ?? '').trim().toLowerCase();
            const optionRows = reps.filter(
              (r) => !filterText || `${r.name} ${r.email} ${r.role ?? ''}`.toLowerCase().includes(filterText),
            );
            return (
              <CollapsibleCard key={key} defaultOpen={false}>
                <CollapsibleCardHeader>
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div>
                      <Text size="sm" weight="semibold">
                        {c.name}
                      </Text>
                      <Text size="xs" variant="tertiary">
                        {campaignWhenAndWhere(c)}
                      </Text>
                    </div>
                    <Text size="xs" variant="tertiary">
                      {mappedReps.length} {mappedReps.length === 1 ? 'person' : 'people'}
                    </Text>
                  </div>
                </CollapsibleCardHeader>
                <CollapsibleCardBody className="p-4 flex flex-col gap-3">
                  {mappedReps.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {mappedReps.map((r) => (
                        <Chip key={r.email} onClear={() => toggleMapping(r.email, c.id)}>
                          {r.name}
                        </Chip>
                      ))}
                    </div>
                  ) : (
                    <Text size="xs" variant="tertiary">
                      No one is scanning this event yet.
                    </Text>
                  )}
                  <Text size="sm" weight="semibold">
                    Who is scanning at this event
                  </Text>
                  <DebouncedSearchInput
                    aria-label="Filter people"
                    placeholder="Filter people…"
                    initialValue={cardFilters[key] ?? ''}
                    onSearch={(v) => setCardFilters((prev) => ({ ...prev, [key]: v }))}
                  />
                  <div className="grid grid-cols-1 @min-[760px]/mgr:grid-cols-2 gap-x-6 gap-y-0.5">
                    {optionRows.length === 0 ? (
                      <Text size="xs" variant="tertiary">
                        No one matches “{cardFilters[key] ?? ''}”.
                      </Text>
                    ) : (
                      optionRows.map((r) => (
                        <div
                          key={r.email}
                          className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-bg-secondary"
                        >
                          <CheckboxField
                            size="sm"
                            label={
                              r.email === currentUserEmail ? (
                                <>
                                  {r.name} <StatusBadge status="neutral" size="xs">you</StatusBadge>
                                </>
                              ) : (
                                r.name
                              )
                            }
                            description={r.role ?? undefined}
                            checked={draftHas(r.email, c.id)}
                            onCheckedChange={() => toggleMapping(r.email, c.id)}
                          />
                          {isPending(r.email, c.id) && (
                            <StatusBadge status="action" size="xs">
                              unsaved
                            </StatusBadge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CollapsibleCardBody>
              </CollapsibleCard>
            );
          })}

        {!isLoading && !byEvent && filteredReps.length === 0 && (
          <Text size="sm" variant="tertiary">
            No one matches “{mapQuery}”.
          </Text>
        )}
        {!isLoading && !byEvent &&
          filteredReps.map((r) => {
            const key = `u_${r.email}`;
            const mappedCampaigns = windowedCampaigns.filter((c) => draftHas(r.email, c.id));
            const windowedIds = new Set(windowedCampaigns.map((c) => c.id));
            const pastCount = Array.from(savedKeys).filter((k) => {
              const cut = k.indexOf('|');
              return k.slice(0, cut) === r.email && !windowedIds.has(k.slice(cut + 1));
            }).length;
            const filterText = (cardFilters[key] ?? '').trim().toLowerCase();
            const optionRows = windowedCampaigns.filter(
              (c) => !filterText || `${c.name} ${c.city ?? ''}`.toLowerCase().includes(filterText),
            );
            return (
              <CollapsibleCard key={key} defaultOpen={false}>
                <CollapsibleCardHeader>
                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div>
                      <Text size="sm" weight="semibold">
                        {r.name} {r.email === currentUserEmail && <StatusBadge status="neutral" size="xs">you</StatusBadge>}
                      </Text>
                      <Text size="xs" variant="tertiary">
                        {r.role ?? ''} · {r.email}
                      </Text>
                    </div>
                    <Text size="xs" variant="tertiary">
                      {mappedCampaigns.length} {mappedCampaigns.length === 1 ? 'event' : 'events'}
                    </Text>
                  </div>
                </CollapsibleCardHeader>
                <CollapsibleCardBody className="p-4 flex flex-col gap-3">
                  {mappedCampaigns.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {mappedCampaigns.map((c) => (
                        <Chip key={c.id} onClear={() => toggleMapping(r.email, c.id)}>
                          {c.name}
                        </Chip>
                      ))}
                    </div>
                  ) : (
                    <Text size="xs" variant="tertiary">
                      Not scanning at any event yet.
                    </Text>
                  )}
                  <Text size="sm" weight="semibold">
                    Events this person is scanning
                  </Text>
                  <DebouncedSearchInput
                    aria-label="Filter events"
                    placeholder="Filter events…"
                    initialValue={cardFilters[key] ?? ''}
                    onSearch={(v) => setCardFilters((prev) => ({ ...prev, [key]: v }))}
                  />
                  <div className="grid grid-cols-1 @min-[760px]/mgr:grid-cols-2 gap-x-6 gap-y-0.5">
                    {optionRows.length === 0 ? (
                      <Text size="xs" variant="tertiary">
                        No events match “{cardFilters[key] ?? ''}”.
                      </Text>
                    ) : (
                      optionRows.map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-bg-secondary"
                        >
                          <CheckboxField
                            size="sm"
                            label={c.name}
                            description={campaignWhenAndWhere(c)}
                            checked={draftHas(r.email, c.id)}
                            onCheckedChange={() => toggleMapping(r.email, c.id)}
                          />
                          {isPending(r.email, c.id) && (
                            <StatusBadge status="action" size="xs">
                              unsaved
                            </StatusBadge>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {pastCount > 0 && (
                    <Text size="xs" variant="tertiary">
                      {pastCount} past {pastCount === 1 ? 'event' : 'events'} not shown · still on record
                    </Text>
                  )}
                </CollapsibleCardBody>
              </CollapsibleCard>
            );
          })}
      </div>

      <div className="sticky bottom-0 z-10 flex items-center gap-2 flex-wrap border-t border-border-primary bg-bg-primary -mx-4 -mb-4 px-4 py-3 @min-[760px]/mgr:mx-0 @min-[760px]/mgr:mb-0">
        <Text size="sm" className="flex-1 basis-full @min-[760px]/mgr:basis-auto">
          {pendingCount > 0
            ? `${pendingCount} unsaved change${pendingCount === 1 ? '' : 's'} · ${diff.added.length} added, ${diff.removed.length} removed`
            : `Saved · ${savedKeys.size} mapping${savedKeys.size === 1 ? '' : 's'} across ${eventCount} event${eventCount === 1 ? '' : 's'}`}
        </Text>
        <Button variant="ghost" disabled={pendingCount === 0} onClick={handleDiscardClick} className="flex-1 @min-[760px]/mgr:flex-none">
          Discard
        </Button>
        <Button
          variant="primary"
          disabled={pendingCount === 0}
          loading={saveMutation.isPending}
          onClick={handleSave}
          className="flex-1 @min-[760px]/mgr:flex-none"
        >
          Save mapping
        </Button>
      </div>

      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        {/* No `onClose` (round-2 live-QA bug 5) — see ScanList.tsx's
            "Delete this draft?" dialog for the full rationale: omitting the X
            makes Cancel the first tabbable element, so Enter defaults to
            Cancel on this destructive confirm, which is the correct behavior
            here (do not make Enter confirm-by-default on a destructive
            action). */}
        <AlertDialogHeader>Discard changes?</AlertDialogHeader>
        <AlertDialogBody>
          <Text size="sm">
            {pendingCount} unsaved change{pendingCount === 1 ? '' : 's'} will be discarded. This can't be
            undone.
          </Text>
        </AlertDialogBody>
        <AlertDialogActions>
          <Button variant="ghost" onClick={() => setConfirmDiscardOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDiscard}>
            Discard
          </Button>
        </AlertDialogActions>
      </AlertDialog>
    </div>
  );
}
